import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { mediaQueryLarge } from '@theme/utilities';

/**
 * Refresh the native carousel without moving an active shopper and track its scroll position.
 * @extends {Component<{progressTrack?: HTMLElement, progressThumb?: HTMLElement}>}
 */
class HenryBestSellers extends Component {
  #controller;
  #connectionVersion = 0;
  #active = false;
  #viewportObserver;
  #started = false;
  #interacted = false;
  #progressController;
  #resizeObserver;
  #progressFrame;
  #scroller;

  connectedCallback() {
    super.connectedCallback();
    const connectionVersion = ++this.#connectionVersion;
    this.#controller = new AbortController();
    const markInteraction = () => {
      this.#interacted = true;
      this.#activate();
    };
    for (const name of ['pointerdown', 'focusin', 'wheel', 'click']) {
      this.addEventListener(name, markInteraction, { passive: true, signal: this.#controller.signal });
    }

    if (
      !mediaQueryLarge.matches &&
      !window.Shopify?.designMode &&
      document.querySelector('#MainContent[data-template="index"]') &&
      typeof IntersectionObserver !== 'undefined'
    ) {
      this.#viewportObserver = new IntersectionObserver((entries) => {
        if (connectionVersion !== this.#connectionVersion) return;
        if (entries.some((entry) => entry.isIntersecting)) this.#activate();
      }, { rootMargin: '600px 0px' });
      this.#viewportObserver.observe(this);
      mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange);
      return;
    }

    this.#activate();
  }

  updatedCallback() {
    super.updatedCallback();
    if (this.#active) this.#setupProgress();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#connectionVersion++;
    this.#controller?.abort();
    this.#stopDeferredStartup();
    this.#stopProgress();
    if (this.dataset.sectionId) sectionRenderer.abortRender(this.dataset.sectionId);
    this.#active = false;
    this.#started = false;
  }

  #handleBreakpointChange = () => {
    if (mediaQueryLarge.matches) this.#activate();
  };

  #stopDeferredStartup() {
    this.#viewportObserver?.disconnect();
    this.#viewportObserver = undefined;
    mediaQueryLarge.removeEventListener('change', this.#handleBreakpointChange);
  }

  #activate = () => {
    if (!this.isConnected || this.#active) return;
    this.#active = true;
    this.#stopDeferredStartup();
    this.#setupProgress();
    this.#refresh();
  };

  #stopProgress() {
    this.#progressController?.abort();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#progressFrame);
    this.#progressFrame = undefined;
  }

  #setupProgress() {
    this.#stopProgress();
    this.#scroller = this.querySelector('.resource-list__carousel > slideshow-container > slideshow-slides');
    if (!this.#scroller || !this.refs.progressTrack || !this.refs.progressThumb) return;

    this.#progressController = new AbortController();
    this.#scroller.addEventListener('scroll', this.#queueProgress, {
      passive: true,
      signal: this.#progressController.signal,
    });
    this.#resizeObserver = new ResizeObserver(this.#queueProgress);
    this.#resizeObserver.observe(this.#scroller);
    this.#queueProgress();
  }

  #queueProgress = () => {
    if (this.#progressFrame !== undefined) return;
    this.#progressFrame = requestAnimationFrame(() => {
      this.#progressFrame = undefined;
      const scroller = this.#scroller;
      const { progressTrack, progressThumb } = this.refs;
      if (!scroller || !progressTrack || !progressThumb) return;

      const scrollWidth = scroller.scrollWidth;
      const clientWidth = scroller.clientWidth;
      const maxScroll = scrollWidth - clientWidth;
      const hidden = maxScroll <= 1;
      // Finish geometry/style reads before updating the progress indicator.
      const position = hidden ? 0 : Math.min(1, Math.max(0, Math.abs(scroller.scrollLeft) / maxScroll));
      const direction = hidden ? 1 : (getComputedStyle(scroller).direction === 'rtl' ? -1 : 1);
      if (progressTrack.hidden !== hidden) progressTrack.hidden = hidden;
      if (hidden) return;

      // A fixed-width segment represents the visible portion, including at both ends.
      const visibleFraction = clientWidth / scrollWidth;
      const offset = direction * position * (1 - visibleFraction) * 100;
      const transform = `translateX(${offset}%) scaleX(${visibleFraction})`;
      if (progressThumb.style.transform !== transform) progressThumb.style.transform = transform;
    });
  };

  async #refresh() {
    if (this.#started || this.#interacted || this.dataset.rankedLive === 'true' || !this.dataset.sourceUrl) return;
    this.#started = true;
    const connectionVersion = this.#connectionVersion;
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;
    const url = new URL(this.dataset.sourceUrl, window.location.origin);
    try {
      await sectionRenderer.renderSection(sectionId, {
        url,
        cache: false,
        mode: 'hydration',
        injectStylesheet: true,
        shouldRender: (html) => this.isConnected && connectionVersion === this.#connectionVersion && !this.#interacted && html.includes('data-ranked-live="true"'),
      });
    } catch {
      // Keep the server-rendered products and sorted collection link available.
    } finally {
      if (this.isConnected && this.#active && connectionVersion === this.#connectionVersion) this.#setupProgress();
    }
  }
}

if (!customElements.get('henry-best-sellers')) customElements.define('henry-best-sellers', HenryBestSellers);
